import { useEffect } from 'react'
import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { computePermissions } from '../domain/permission'
import { checkPermission } from '../application/check-permission'
import { PERMISSIONS_DENIED } from '../domain/permission'
import { getPermissionConfig } from '../application/get-permission-config'
import type { PermissionKey, ResolvedPermissions, UserContext } from '../domain/permission'
import { permissionConfig$, permissions$ } from '../store'
import { notify, permissions as permissionsPort } from '../wiring'

// Called once at the app shell level (GlobalHooks). Loads the config, then
// recomputes resolved permissions whenever the user or config changes.
// No other hook should fetch - they only read from permissions$.
const useInitPermissions = () => {
	const user = useSelector(() => session$.user.get())
	const config = useSelector(permissionConfig$)

	useEffect(() => {
		getPermissionConfig(permissionsPort, notify).then(c => {
			if (c) permissionConfig$.set(c)
		})
	}, [])

	useEffect(() => {
		if (!user) {
			permissions$.set(PERMISSIONS_DENIED)
			return
		}

		const ctx: UserContext = {
			userId: user.id,
			companyRole: user.companyRole,
			deptRole: user.deptRole,
			departmentId: user.departmentId,
			teamIds: user.teamIds,
		}

		permissions$.set(computePermissions(config, ctx))
	}, [user, config])
}

const usePermissions = (): ResolvedPermissions => useSelector(permissions$)

const usePermission = (key: PermissionKey): boolean => {
	const resolved = usePermissions()
	return checkPermission(resolved, key)
}

export { useInitPermissions, usePermission, usePermissions }
